import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";

export interface CustomerInstructionData {
  creation_time: string;
  customer_instruction: string;
  update_datetime: string;
  customer_id: string;
  SK: string;
  PK: string;
  customer_name: string;
  status_automation: string;
}

interface InstructionsData {
  customerInstructions: CustomerInstructionData | null;
  groupInstructions: CustomerInstructionData | null;
  isLoading: boolean;
  error: Error | null;
  hasInstructions: boolean;
}

export const useCustomerInstructions = (customerId?: string, customerGroupId?: string) => {
  const axiosPrivate = useAxiosPrivate();
  
  const customerQuery = useQuery<CustomerInstructionData, Error>({
    queryKey: ["customerInstructions", customerId],
    queryFn: async () => {
      if (!customerId) {
        throw new Error("No customer ID provided");
      }
      const response = await axiosPrivate.get(`/customers/settings/${customerId}`);
      return response.data;
    },
    enabled: !!customerId,
    staleTime: 300000, // 5 minutes
  });
  
  const groupQuery = useQuery<CustomerInstructionData, Error>({
    queryKey: ["customerGroupInstructions", customerGroupId],
    queryFn: async () => {
      if (!customerGroupId) {
        throw new Error("No customer group ID provided");
      }
      const response = await axiosPrivate.get(`/customers_group/settings/${customerGroupId}`);
      return response.data;
    },
    enabled: !!customerGroupId,
    staleTime: 300000, // 5 minutes
  });
  
  const hasInstructions = !!(
    (customerQuery.data?.customer_instruction && customerQuery.data.customer_instruction.trim() !== "") || 
    (groupQuery.data?.customer_instruction && groupQuery.data.customer_instruction.trim() !== "")
  );
  
  return {
    customerInstructions: customerQuery.data ?? null,
    groupInstructions: groupQuery.data ?? null,
    isLoading: customerQuery.isLoading || groupQuery.isLoading,
    error: customerQuery.error ?? groupQuery.error ?? null,
    hasInstructions
  };
};

export default useCustomerInstructions;